/**
 * CR-6444: sectioned layout for the Quality → NCR filter panel.
 *
 * Regroups the existing filters into the five sections from the supplied
 * design — Date Range · Company · Supplier · Classification · NCR Details —
 * without changing any control type or filter behaviour.
 *
 * Two things are fixed on the way through, both visible to users today:
 *  - Commodity Group / Commodity Type and the Strategic-suppliers tickbox were
 *    each rendered TWICE, bound to the same state, so the panel showed
 *    duplicate controls that moved together. Each now appears once.
 *  - The later rows were styled with hardcoded `text-gray-700` / `bg-white`,
 *    which ignores the theme and reads wrong in dark mode. All controls now use
 *    the shared filter styling.
 *
 * Six filters the design omits are deliberately kept — Search, UK/EU, Claim
 * Status, Action Status and the two Collection Date fields. Removing working
 * filters would breach the ticket's own "no regressions" criterion, so they are
 * placed in the section they belong to and raised on the ticket instead.
 */
import { Calendar, Building2, Truck, Tags, FileText } from 'lucide-react';
import SearchableSelect from '../ui/SearchableSelect';
import { FilterSection } from '../filters/FilterSection';
import type { NCRFiltersState } from './NCRFiltersBar';
import { useFilterVisibility } from '../../hooks/useFilterVisibility';
import { NO_SUPPLIER_FILTER_VALUE, NO_SUPPLIER_LABEL } from './ncrNoSupplier';

/** Horizontal divider with padding so it doesn't touch card edges */
const SectionDivider = () => (
  <div className="mx-4 border-t border-border" />
);

/**
 * Compact styling matching the Deliveries filter panel density.
 * NCR filters sit above a data grid and need tighter spacing than the dashboard.
 */
const inputCls =
  'w-full px-3 py-2.5 text-sm border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary bg-background';
const labelCls = 'text-xs font-medium mb-1 block text-foreground';
const gridCls = 'grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3';

interface IdName { id: number; name: string; code?: string | null }
interface SupplierProfileOption { company_id?: number | null; company?: number | null; company_name: string }
interface LabelledOption { value: string; label: string }

export interface NCRFilterSectionsProps {
  /** E4: what this customer calls its own NCR number (default "Customer NCR No."). */
  customerReferenceLabel?: string;
  values: NCRFiltersState;
  onChange: (next: Partial<NCRFiltersState>) => void;
  quickRanges: ReadonlyArray<{ id: string; label: string }>;
  activeQuickRange: string | null;
  onQuickRange: (id: string) => void;
  // CR-6444: Division → Site → Warehouse cascade support
  divisions: IdName[];
  // CR-6444: Company Country options for Company section
  companyCountryOptions: ReadonlyArray<LabelledOption>;
  sites: IdName[];
  warehouses: IdName[];
  supplierGroups: IdName[];
  filteredSuppliers: SupplierProfileOption[];
  // CR-6444: Supplier Country options for cascade
  supplierCountryOptions: ReadonlyArray<LabelledOption>;
  // CR-6444: Supplier Type options for Classification section
  supplierTypeOptions: ReadonlyArray<LabelledOption>;
  commodityGroups: IdName[];
  filteredCommodityTypes: IdName[];
  liabilityOptions: ReadonlyArray<LabelledOption>;
  /**
   * CR-6451: false on the supplier portal. A supplier filtering NCRs by
   * "strategic suppliers only" is filtering on a property of themselves —
   * the same reasoning that removed supplier-country from supplier-portal
   * pages in CR-4887 — and the supplier design omits it.
   */
  showStrategicFilter?: boolean;
  // CR-6444: cascade change handlers
  onDivisionChange: (value: string) => void;
  onCompanyCountryChange: (value: string) => void;
  onSiteChange: (value: string) => void;
  onSupplierGroupChange: (value: string) => void;
  onSupplierCountryChange: (value: string) => void;
  onCommodityGroupChange: (value: string) => void;
}

export function NCRFilterSections({
  customerReferenceLabel: ncrCustomerReferenceLabel = 'Customer NCR No.',
  values,
  onChange,
  quickRanges,
  activeQuickRange,
  onQuickRange,
  divisions,
  companyCountryOptions,
  sites,
  warehouses,
  supplierGroups,
  filteredSuppliers,
  supplierCountryOptions,
  supplierTypeOptions,
  commodityGroups,
  filteredCommodityTypes,
  liabilityOptions,
  showStrategicFilter = true,
  onDivisionChange,
  onCompanyCountryChange,
  onSiteChange,
  onSupplierGroupChange,
  onSupplierCountryChange,
  onCommodityGroupChange,
}: NCRFilterSectionsProps) {
  // CR-4802: filters this tenant shows — one answer for every panel.
  const fv = useFilterVisibility();
  /** Native <select> bound to one filter key — unchanged element and handler. */
  const select = (
    label: string,
    key: keyof NCRFiltersState,
    placeholder: string,
    options: ReadonlyArray<LabelledOption>,
    extra?: Partial<NCRFiltersState>,
  ) => (
    <div>
      <label className={labelCls}>{label}</label>
      <select
        value={String(values[key] ?? '')}
        onChange={(e) => onChange({ [key]: e.target.value, ...(extra || {}) } as Partial<NCRFiltersState>)}
        className={inputCls}
      >
        <option value="">{placeholder}</option>
        {options.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    </div>
  );

  const dateField = (label: string, key: 'dateFrom' | 'dateTo' | 'collectionDateFrom' | 'collectionDateTo') => (
    <div>
      <label className={labelCls}>{label}</label>
      <input
        type="date"
        value={values[key]}
        onChange={(e) => onChange({ [key]: e.target.value } as Partial<NCRFiltersState>)}
        className={inputCls}
      />
    </div>
  );

  return (
    <div className="[&>section]:border-t-0">
      {/* Divider above first section */}
      <SectionDivider />

      {/* Date Range section */}
      <FilterSection title="Date Range" icon={Calendar}>
        <div className="flex flex-wrap gap-2 mb-4">
          {quickRanges.map(({ id, label }) => (
            <button
              key={id}
              onClick={() => onQuickRange(id)}
              className={`px-3 py-1.5 text-xs font-medium border rounded-md transition-colors ${
                activeQuickRange === id
                  ? 'border-primary bg-primary/10 text-primary hover:bg-primary/20'
                  : 'border-border hover:border-primary hover:bg-primary/5 hover:text-primary'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
        <div className={gridCls}>
          {dateField('Date From', 'dateFrom')}
          {dateField('Date To', 'dateTo')}
        </div>
      </FilterSection>

      <SectionDivider />

      {/* Company section */}
      <FilterSection title="Company" icon={Building2}>
        <div className={gridCls}>
          {/* CR-6444: Division dropdown for Division → Site cascade */}
          {fv.division && divisions.length > 0 && (
            <div>
              <label className={labelCls}>Division</label>
              <SearchableSelect
                options={divisions.map((d) => ({ value: String(d.id), label: d.name }))}
                value={values.divisionFilter}
                onChange={onDivisionChange}
                placeholder="All Divisions"
              />
            </div>
          )}
          {/* CR-6444: Company Country filter */}
          {fv.companyCountry && companyCountryOptions.length > 0 && (
            <div>
              <label className={labelCls}>Country</label>
              <SearchableSelect
                options={[...companyCountryOptions]}
                value={values.companyCountryFilter}
                onChange={onCompanyCountryChange}
                placeholder="All Countries"
              />
            </div>
          )}
          <div>
            <label className={labelCls}>Site</label>
            <SearchableSelect
              options={sites.map((s) => ({ value: String(s.id), label: s.name }))}
              value={values.siteFilter}
              onChange={onSiteChange}
              placeholder="All Sites"
            />
          </div>
          {fv.warehouse && select('Warehouse', 'warehouseFilter', 'All Warehouses',
            warehouses.map((w) => ({
              value: String(w.id),
              label: w.code ? `${w.code} — ${w.name}` : w.name,
            })))}
        </div>
      </FilterSection>

      <SectionDivider />

      {/* Supplier — CR-6444: cascades for Supplier Country → Supplier Site and
          Supplier Group → Supplier Site. Selecting a parent clears the child
          if it becomes invalid. */}
      <FilterSection title="Supplier" icon={Truck}>
        <div className={gridCls}>
          <div>
            <label className={labelCls}>Supplier Group</label>
            <SearchableSelect
              options={supplierGroups.map((g) => ({ value: String(g.id), label: g.name }))}
              value={values.supplierGroupFilter}
              onChange={onSupplierGroupChange}
              placeholder="All Supplier Groups"
            />
          </div>
          {/* CR-6444: Supplier Country filter for cascade */}
          {supplierCountryOptions.length > 0 && (
            <div>
              <label className={labelCls}>Supplier Country</label>
              <SearchableSelect
                options={[...supplierCountryOptions]}
                value={values.supplierCountryFilter}
                onChange={onSupplierCountryChange}
                placeholder="All Countries"
              />
            </div>
          )}
          <div>
            <label className={labelCls}>Supplier Site</label>
            <SearchableSelect
              // quality.no_supplier: customer layout only (the supplier
              // portal renders NCRFilterSupplierLayout, which never offers it).
              options={[
                { value: NO_SUPPLIER_FILTER_VALUE, label: NO_SUPPLIER_LABEL },
                ...filteredSuppliers
                  .filter((s) => (s.company_id ?? s.company) != null)
                  .map((s) => ({ value: String(s.company_id ?? s.company), label: s.company_name })),
              ]}
              value={values.supplierFilter}
              onChange={(val) => onChange({ supplierFilter: val })}
              placeholder="All Supplier Sites"
            />
          </div>
          {/* CR-6444: Supplier Code text filter — CR-4802: tenant-gated */}
          {fv.supplierCode && (
          <div>
            <label className={labelCls}>Supplier Code</label>
            <input
              type="text"
              placeholder="Supplier code..."
              value={values.supplierCodeFilter}
              onChange={(e) => onChange({ supplierCodeFilter: e.target.value })}
              className={inputCls}
            />
          </div>
          )}
          {/* CR-7350: the two quality-engineer responsibilities. Free text
              rather than a dropdown: both are free-text names written by the
              Supplier Quality Master import, not Helix users, so there is no
              authoritative list to offer. Contains-match, resolved exactly as
              the columns display them. */}
          <div>
            <label className={labelCls}>Quality Engineer</label>
            <input
              type="text"
              placeholder="Quality engineer..."
              value={values.qualityEngineerFilter}
              onChange={(e) => onChange({ qualityEngineerFilter: e.target.value })}
              className={inputCls}
            />
          </div>
          <div>
            <label className={labelCls}>Support Quality Engineer</label>
            <input
              type="text"
              placeholder="Support quality engineer..."
              value={values.supportQualityEngineerFilter}
              onChange={(e) => onChange({ supportQualityEngineerFilter: e.target.value })}
              className={inputCls}
            />
          </div>
        </div>
        {showStrategicFilter && (
        <label className="flex items-center gap-2 mt-3 text-xs font-medium cursor-pointer">
          <input
            type="checkbox"
            checked={values.strategicFilter}
            onChange={(e) => onChange({ strategicFilter: e.target.checked })}
            className="h-3.5 w-3.5 rounded border-border"
          />
          Strategic suppliers only
        </label>
        )}
      </FilterSection>

      {/* CR-4802: the whole group is tenant-gated; drop the heading when nothing is shown. */}
      {((fv.supplierType && supplierTypeOptions.length > 0) || (fv.commodity && commodityGroups.length > 0)) && (<>
        <SectionDivider />

        {/* Classification section */}
        <FilterSection title="Classification" icon={Tags}>
          <div className={gridCls}>
            {/* CR-6444: Supplier Type filter per design */}
            {fv.supplierType && supplierTypeOptions.length > 0 && (
              <div>
                <label className={labelCls}>Supplier Type</label>
                <SearchableSelect
                  options={[...supplierTypeOptions]}
                  value={values.supplierTypeFilter}
                  onChange={(val) => onChange({ supplierTypeFilter: val })}
                  placeholder="All Supplier Types"
                />
              </div>
            )}
            {/* CR-4802: hidden together when the tenant hasn't configured any
                commodity groups in Admin Setup — commodityGroups here is the
                full tenant list NCRFiltersBar fetches for the dropdown, not
                narrowed by selection, so it's a valid "configured at all"
                signal (mirrors the Dashboard ClassificationSection gate). */}
            {fv.commodity && commodityGroups.length > 0 && (
              <div>
                <label className={labelCls}>Commodity Group</label>
                <SearchableSelect
                  options={commodityGroups.map((g) => ({ value: String(g.id), label: g.name }))}
                  value={values.commodityGroupFilter}
                  onChange={onCommodityGroupChange}
                  placeholder="All Commodity Groups"
                />
              </div>
            )}
            {fv.commodity && commodityGroups.length > 0 && select('Commodity Type', 'commodityTypeFilter', 'All Commodity Types',
              filteredCommodityTypes.map((t) => ({ value: String(t.id), label: t.name })))}
          </div>
        </FilterSection>
      </>)}

      <SectionDivider />

      {/* NCR Details section */}
      <FilterSection title="NCR Details" icon={FileText}>
        <div className={gridCls}>
          {select('Status', 'statusFilter', 'All Statuses', [
        { value: 'open', label: 'Open' },
        { value: 'draft', label: 'Draft' },
        { value: 'created', label: 'Created' },
        { value: 'submitted', label: 'Submitted (no 8D)' },
        { value: 'ca_submitted', label: 'CA Submitted' },
        { value: 'car_submitted', label: 'CAR Submitted' },
        { value: 'eightd_complete', label: '8D Complete' },
        { value: 'closed', label: 'Closed' },
        { value: 'deleted', label: 'Deleted' },
          ])}
          <div>
            <label className={labelCls}>Part Number</label>
            <input
              type="text"
              placeholder="Part number..."
              value={values.partNumberFilter}
              onChange={(e) => onChange({ partNumberFilter: e.target.value })}
              className={inputCls}
            />
          </div>
          {/* CR-6444, E4 (30 Sep 2026): the customer's own name for its NCR number. */}
          <div>
            <label className={labelCls}>{ncrCustomerReferenceLabel}</label>
            <input
              type="text"
              placeholder={ncrCustomerReferenceLabel}
              value={values.srnFilter}
              onChange={(e) => onChange({ srnFilter: e.target.value })}
              className={inputCls}
            />
          </div>
          {/* CR-6444: NCR ID per design */}
          <div>
            <label className={labelCls}>NCR ID</label>
            <input
              type="text"
              placeholder="NCR ID..."
              value={values.ncrIdFilter}
              onChange={(e) => onChange({ ncrIdFilter: e.target.value })}
              className={inputCls}
            />
          </div>
          {select('Liability', 'liabilityFilter', 'All Liabilities', liabilityOptions)}
        </div>
        {/* CR-6444: Requirements checkboxes per design */}
        <div className="-mx-2 mt-4 mb-3 border-t border-border" />
        <div>
          <span className="text-xs font-medium text-foreground block mb-2">Requirements</span>
          <div className="flex flex-wrap gap-x-6 gap-y-2">
            <label className="flex items-center gap-2 cursor-pointer text-xs font-medium">
              <input
                type="checkbox"
                checked={values.creditRequired}
                onChange={(e) => onChange({ creditRequired: e.target.checked })}
                className="h-3.5 w-3.5 rounded border-border"
              />
              Credit required
            </label>
            <label className="flex items-center gap-2 cursor-pointer text-xs font-medium">
              <input
                type="checkbox"
                checked={values.eightDRequired}
                onChange={(e) => onChange({ eightDRequired: e.target.checked })}
                className="h-3.5 w-3.5 rounded border-border"
              />
              8D required
            </label>
            <label className="flex items-center gap-2 cursor-pointer text-xs font-medium">
              <input
                type="checkbox"
                checked={values.collectionRequired}
                onChange={(e) => onChange({ collectionRequired: e.target.checked })}
                className="h-3.5 w-3.5 rounded border-border"
              />
              Collection required
            </label>
            <label className="flex items-center gap-2 cursor-pointer text-xs font-medium">
              <input
                type="checkbox"
                checked={values.replacementRequired}
                onChange={(e) => onChange({ replacementRequired: e.target.checked })}
                className="h-3.5 w-3.5 rounded border-border"
              />
              Replacement required
            </label>
          </div>
        </div>
      </FilterSection>
    </div>
  );
}
